export const buildTemplateJSON = (state) => {
  const {
    name,
    language,
    category,
    variableType,
    mediaSample,
    headerText,
    headerVariables,
    headerMediaUrl,
    body,
    bodyVariables,
    footer,
    ctaButtons,
    quickReplyButtons,
  } = state;

  if (category === "AUTHENTICATION") {
    return buildAuthenticationJSON(state);
  }

  const isNamed = variableType === "named";
  const payload = {
    name,
    language,
    category: category.toUpperCase(),
  };

  if (isNamed) {
    payload.parameter_format = "named";
  }

  const components = [];

  if (mediaSample === "none" && headerText && headerText.trim()) {
    const headerComp = {
      type: "HEADER",
      format: "TEXT",
      text: headerText,
    };
    if (headerVariables.length > 0) {
      if (isNamed) {
        headerComp.example = {
          header_text_named_params: headerVariables.map((v) => ({
            param_name: v.name,
            example: String(v.sample || "").trim() || String(v.name || "Sample").replace(/_/g, " "),
          })),
        };
      } else {
        headerComp.example = {
          header_text: headerVariables.map((v, i) => String(v.sample || "").trim() || `Sample ${i + 1}`),
        };
      }
    }
    components.push(headerComp);
  } else if (mediaSample !== "none") {
    const formatMap = {
      image: "IMAGE",
      video: "VIDEO",
      document: "DOCUMENT",
      location: "LOCATION",
    };
    const headerComp = {
      type: "HEADER",
      format: formatMap[mediaSample] || "TEXT",
    };
    // NOTE: We intentionally do not put `example.header_url` here. Meta only
    // accepts `header_handle` for IMAGE/VIDEO/DOCUMENT headers — sending both
    // gets the template rejected with an OAuthException. The server picks up
    // the source media via the separate `header_media_url` FormData field
    // (or an uploaded `headerFile`), uploads it to Meta, and writes
    // `example.header_handle` itself. The on-screen preview reads from
    // `headerFilePreview` / `headerMediaUrl` in state, not from this JSON.
    components.push(headerComp);
  }

  if (body && body.trim()) {
    const bodyComp = {
      type: "BODY",
      text: body,
    };
    if (bodyVariables.length > 0) {
      if (isNamed) {
        bodyComp.example = {
          body_text_named_params: bodyVariables.map((v) => ({
            param_name: v.name,
            example: String(v.sample || "").trim() || String(v.name || "Sample").replace(/_/g, " "),
          })),
        };
      } else {
        bodyComp.example = {
          body_text: [bodyVariables.map((v, i) => String(v.sample || "").trim() || `Sample ${i + 1}`)],
        };
      }
    }
    components.push(bodyComp);
  }

  if (footer && footer.trim()) {
    components.push({ type: "FOOTER", text: footer });
  }

  const allButtons = [];

  const COPY_CODE_STATIC_TEXT = "Copy offer code";

  ctaButtons.forEach((btn) => {
    // Meta API uses COPY_CODE and "example"; button text must be exactly "Copy offer code"
    const metaType =
      btn.type === "COPY_OFFER_CODE" ? "COPY_CODE" : btn.type;
    const buttonObj = {
      type: metaType,
      text:
        btn.type === "COPY_OFFER_CODE" ? COPY_CODE_STATIC_TEXT : btn.text,
    };

    if (btn.type === "URL") {
      if (btn.url) buttonObj.url = btn.url;
      if (btn.url_type === "dynamic" && btn.urlVariableSample) {
        buttonObj.example = [btn.urlVariableSample];
      }
    } else if (btn.type === "PHONE_NUMBER") {
      const countryCode = btn.country_code || "+91";
      const phone = (btn.phone_number || "").replace(/\D/g, "");
      buttonObj.phone_number = phone ? `${countryCode}${phone}` : "";
    } else if (btn.type === "COPY_OFFER_CODE") {
      // Meta expects "example", not "offer_code"
      if (btn.offer_code) buttonObj.example = btn.offer_code;
    } else if (btn.type === "FLOW") {
      if (btn.flow_id) buttonObj.flow_id = btn.flow_id;
    }

    allButtons.push(buttonObj);
  });

  quickReplyButtons.forEach((btn) => {
    allButtons.push({ type: "QUICK_REPLY", text: btn.text });
  });

  if (allButtons.length > 0) {
    components.push({ type: "BUTTONS", buttons: allButtons });
  }

  payload.components = components;
  return payload;
};

const buildAuthenticationJSON = (state) => {
  const payload = {
    name: state.name,
    language: state.language,
    category: "AUTHENTICATION",
    components: [],
  };

  payload.components.push({
    type: "BODY",
    add_security_recommendation: state.addSecurityRecommendation,
  });

  if (state.addExpiryTime && state.expiryMinutes) {
    payload.components.push({
      type: "FOOTER",
      code_expiration_minutes: state.expiryMinutes,
    });
  }

  payload.components.push({
    type: "BUTTONS",
    buttons: [
      {
        type: "OTP",
        otp_type: "COPY_CODE",
        text: "Copy Code",
      },
    ],
  });

  return payload;
};
